import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  billingRuleService,
  UpdateBillingRuleRequest,
  BillingRule,
  EtriggerEventBillingRule,
} from "@/services/billingRule";
import {
  MessageTemplate,
  MESSAGE_TEMPLATE_TYPE_LABEL,
  EMessageTemplateType,
} from "@/services/messageTemplate";

const ALLOWED_TEMPLATE_TYPES = [
  EMessageTemplateType.PAYMENT,
  EMessageTemplateType.PAYMENT_REMINDER,
];

interface UpdateBillingRuleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: BillingRule;
  templates: MessageTemplate[];
}

function toFormData(rule: BillingRule): UpdateBillingRuleRequest {
  return {
    templateId: Number(rule.templateId),
    name: rule.name,
    daysOffset: rule.daysOffset,
    triggerEvent: EtriggerEventBillingRule.SUBSCRIPTION_EXPIRING,
    sendTime: rule.sendTime,
    isActive: rule.isActive,
  };
}

export function UpdateBillingRuleModal({
  open,
  onOpenChange,
  rule,
  templates,
}: UpdateBillingRuleModalProps) {
  const queryClient = useQueryClient();

  const availableTemplates = templates.filter((tpl) =>
    ALLOWED_TEMPLATE_TYPES.includes(tpl.type),
  );

  const [formData, setFormData] = useState<UpdateBillingRuleRequest>(toFormData(rule));

  useEffect(() => {
    if (open) {
      setFormData(toFormData(rule));
    }
  }, [open, rule]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const updateMutation = useMutation({
    mutationFn: (data: UpdateBillingRuleRequest) =>
      billingRuleService.update(rule.id, data),
    onSuccess: () => {
      toast.success("Regla de cobro actualizada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["billing-rules"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!formData.name?.trim()) {
      toast.error("El nombre de la regla es requerido");
      return;
    }
    if (!formData.templateId) {
      toast.error("Selecciona una plantilla");
      return;
    }
    if (!formData.sendTime?.trim()) {
      toast.error("La hora de envío es requerida");
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Actualizar regla de cobro</DialogTitle>
          <DialogDescription>
            Modifica los datos de la regla de envío automático.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Recordatorio 3 días antes"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>
              Plantilla <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.templateId ? String(formData.templateId) : undefined}
              onValueChange={(val) =>
                setFormData((prev) => ({ ...prev, templateId: Number(val) }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar plantilla" />
              </SelectTrigger>
              <SelectContent>
                {availableTemplates.map((tpl) => (
                  <SelectItem key={tpl.id} value={String(tpl.id)}>
                    {tpl.name} — {MESSAGE_TEMPLATE_TYPE_LABEL[tpl.type] ?? tpl.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sendTime">
              Hora de envío <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sendTime"
              type="time"
              value={formData.sendTime}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, sendTime: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="daysOffset">
              Días relativos al vencimiento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="daysOffset"
              type="number"
              value={formData.daysOffset}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  daysOffset: Number(e.target.value),
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              3 = 3 días antes, 0 = el día mismo, -2 = 2 días después (mora).
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
            <div>
              <Label htmlFor="isActive" className="text-sm">Regla activa</Label>
              <p className="text-xs text-muted-foreground">
                Las reglas inactivas no disparan envíos.
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
